import React, { useState } from 'react';
import API_BASE from '../../../utils/api.js';
import QuickBooksConnectModal from '../../utils/QuickbooksConnectModal';
import notify from '../../../utils/notify';
import { isQuickBooksConnectionError } from '../../../utils/quickbooksErrors';

function getQuickBooksInvoiceUrl(invoiceId, env = 'sandbox') {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';

  return `${base}/app/invoice?txnId=${invoiceId}`;
}

export default function InvoiceActions({
  onClose,
  saveDraft,
  emailClient,
  onDownloadPDF,
  onConvertToPO,
  convertingPO,
  saving,
  invoiceId,
  projectId,
  quickbooks,
  canEdit = true,
}) {
  const token = localStorage.getItem('idToken');

  const [syncingQB, setSyncingQB] = useState(false);
  const [showQBModal, setShowQBModal] = useState(false);

  async function createQuickBooksInvoice() {
    if (!invoiceId || !projectId) {
      notify('Please save the invoice before syncing to QuickBooks.');
      return;
    }

    if (!token) {
      notify('You must be signed in to sync with QuickBooks.');
      return;
    }

    try {
      setSyncingQB(true);

      const res = await fetch(`${API_BASE}/api/quickbooks/create-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoiceId,
          projectId,
        }),
      });

      const rawBody = await res.text();
      let data = {};
      try {
        data = rawBody ? JSON.parse(rawBody) : {};
      } catch {
        data = { error: rawBody || null };
      }

      if (!res.ok) {
        if (isQuickBooksConnectionError(data)) {
          setShowQBModal(true);
          return;
        }

        const message =
          data?.error ||
          data?.details?.error ||
          data?.details?.Fault?.Error?.[0]?.Detail ||
          rawBody ||
          'Failed to create QuickBooks invoice.';
        const alreadySynced = /already synced/i.test(message);
        if (alreadySynced) {
          const updateRes = await fetch(`${API_BASE}/api/quickbooks/update-invoice`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              invoiceId,
              projectId,
            }),
          });

          const updateRaw = await updateRes.text();
          let updateData = {};
          try {
            updateData = updateRaw ? JSON.parse(updateRaw) : {};
          } catch {
            updateData = { error: updateRaw || null };
          }

          if (!updateRes.ok) {
            notify(
              updateData?.error ||
                updateData?.details?.error ||
                updateData?.details?.Fault?.Error?.[0]?.Detail ||
                updateRaw ||
                'Failed to update QuickBooks invoice.'
            );
            return;
          }
        } else {
          console.error('QuickBooks error:', data);
          notify(message);
          return;
        }
      }

      notify('QuickBooks invoice synced.');
      window.location.reload();
    } catch (err) {
      console.error('QuickBooks sync error:', err);
      notify('QuickBooks sync failed.');
    } finally {
      setSyncingQB(false);
    }
  }

  return (
    <>
      <div className="po-actions">
        <button className="if-btn-secondary" onClick={onClose}>
          Cancel
        </button>

        {canEdit && (
          <button className="if-btn-secondary" onClick={() => saveDraft(true)} disabled={saving}>
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
        )}

        {canEdit && (
          <button className="if-btn-primary" onClick={() => saveDraft(false)} disabled={saving}>
            {saving ? 'Saving...' : 'Save & Close'}
          </button>
        )}

        {canEdit && (
          <button className="if-btn-secondary" onClick={emailClient} disabled={saving}>
            Email Client
          </button>
        )}

        <button className="if-btn-secondary" onClick={onDownloadPDF} disabled={saving}>
          Download PDF
        </button>

        {canEdit && (
          <button
            className="if-btn-secondary"
            onClick={onConvertToPO}
            disabled={saving || convertingPO}
          >
            {convertingPO ? 'Converting...' : 'Convert to Purchase Order'}
          </button>
        )}

        <div className="po-qb-divider" />

        {quickbooks?.invoiceId && quickbooks?.environment && (
          <a
            className="if-btn-success"
            href={getQuickBooksInvoiceUrl(
              quickbooks.invoiceId,
              quickbooks.environment
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            View in QuickBooks
          </a>
        )}

        <button
          className="if-btn-qb"
          onClick={createQuickBooksInvoice}
          disabled={saving || syncingQB}
        >
          {syncingQB ? 'Syncing...' : 'Sync QuickBooks Invoice'}
        </button>
      </div>

      <QuickBooksConnectModal
        open={showQBModal}
        onClose={() => setShowQBModal(false)}
        onConnected={createQuickBooksInvoice}
        mode="invoice"
      />
    </>
  );
}
